// The agent's own questions for the user, with answers to click (David, 16 Oct): Claude Code's AskUserQuestion and
// Codex's request_user_input on the user's computer, and ask_user on an API key, which takes the same shape as
// Claude Code's. Shown as one card in the chat; the answer goes back in the same turn and stays in the history.

import type { BetaTool } from "@anthropic-ai/sdk/resources/beta/messages/messages";
import type { AgentQuestion } from "../../shared/types";

/** The tools that ask the user, by any agent's name for them. */
export const ASK_TOOLS = new Set(["ask_user", "AskUserQuestion"]);

/** The chat's own question tool on an API key, the shape of Claude Code's AskUserQuestion. */
export const ASK_USER: BetaTool = {
  name: "ask_user",
  description:
    "Ask the user a short question with a few options to click, when you can't tell what they mean and a guess could be wrong: which list, which board, which of two widgets. " +
    "They can also type an answer of their own. You get their answer back here and carry on with it. Ask only when it matters, never to confirm what is clear. " +
    "Up to four questions at once (which board, and which list on it); multiSelect when more than one option can be right.",
  input_schema: {
    type: "object",
    properties: {
      questions: {
        type: "array",
        items: {
          type: "object",
          properties: {
            question: { type: "string", description: "The question, short and plain." },
            header: { type: "string", description: "A word or two that names it, such as List or Board." },
            options: {
              type: "array",
              items: { type: "object", properties: { label: { type: "string" }, description: { type: "string" } }, required: ["label"] },
            },
            multiSelect: { type: "boolean" },
          },
          required: ["question", "options"],
        },
      },
    },
    required: ["questions"],
  },
};

const text = (x: unknown, n: number) => (typeof x === "string" ? x.trim().slice(0, n) : "");

/** The questions an ask_user call carries, cleaned; none when it carries no question with words. */
export function questionsOf(input: Record<string, unknown>): AgentQuestion[] {
  const list = Array.isArray(input.questions) ? input.questions.slice(0, 4) : [];
  return list.flatMap((x, i): AgentQuestion[] => {
    const q = (x ?? {}) as Record<string, unknown>;
    const question = text(q.question, 300);
    if (!question) return [];
    const options = (Array.isArray(q.options) ? q.options : []).slice(0, 8).flatMap((o) => {
      const r = (o ?? {}) as Record<string, unknown>;
      const label = text(typeof o === "string" ? o : r.label, 80);
      return label ? [{ label, description: text(r.description, 200) }] : [];
    });
    return [{ id: `q${i}`, header: text(q.header, 30), question, options, multi: q.multiSelect === true, other: true }];
  });
}

/** The user's answers, as a line: "Which list? Groceries". */
export function answerText(asked: AgentQuestion[], answers: Record<string, string[]>): string {
  // one question: the answer alone reads best under it
  if (asked.length === 1) return (answers[asked[0].id] ?? []).join(", ") || "(no answer)";
  return asked.map((q) => `${q.question} ${(answers[q.id] ?? []).join(", ") || "(no answer)"}`).join(" · ");
}
